import { Alert, Autocomplete, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { AuditLogSystemArea, RequestSystemAuditingReportRequest, Store } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { formatDateForReport } from '../../../../utils';
import { UserContext } from '../../../../components/shared/useUser';
import { RoleEnum } from '../../../../enums/RoleEnum';

type IProps = {
  onClose?: () => void;
}

type ISystemAreas = { key: number; value: string; roles: RoleEnum[]; isStoresSelect: boolean; isFranchiseSelect: boolean; };

const SystemAuditReportForm = (props: IProps) => {
  const areas: ISystemAreas[] = [
    {
      key: 1,
      value: 'PurchaseOrder',
      roles: [RoleEnum.PurchaseOrderWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 2,
      value: 'WasteChecksheet',
      roles: [RoleEnum.DailyWasteCheckSheetWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 3,
      value: 'Transfers',
      roles: [RoleEnum.TransferWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 4,
      value: 'StockCountSchedule',
      roles: [RoleEnum.StockCountScheduleWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 5,
      value: 'RoleManagement',
      roles: [RoleEnum.RolesAdmin],
      isStoresSelect: false,
      isFranchiseSelect: true,
    },
    {
      key: 6,
      value: 'TransactionReasonManagement',
      roles: [RoleEnum.TransactionWrite, RoleEnum.TransactionAdmin],
      isStoresSelect: false,
      isFranchiseSelect: true,
    },
    {
      key: 7,
      value: 'ScheduleReportManagement',
      roles: [RoleEnum.AutomatedTasks],
      isStoresSelect: false,
      isFranchiseSelect: false,
    },
    {
      key: 8,
      value: 'UserManagement',
      roles: [RoleEnum.UserAdmin],
      isStoresSelect: false,
      isFranchiseSelect: false,
    },
    {
      key: 9,
      value: 'NotificationManagement',
      roles: [RoleEnum.NotificationWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 10,
      value: 'CategoryManagment',
      roles: [RoleEnum.CategoryAdmin],
      isStoresSelect: false,
      isFranchiseSelect: true,
    },
    {
      key: 11,
      value: 'SystemConfig',
      roles: [RoleEnum.ConfigurationAdmin],
      isStoresSelect: false,
      isFranchiseSelect: true,
    },
    {
      key: 12,
      value: 'RecipeChange',
      roles: [RoleEnum.RecipeAdmin, RoleEnum.RecipeWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 13,
      value: 'StockCount',
      roles: [RoleEnum.StockCountAdmin, RoleEnum.StockCountWrite],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
    {
      key: 14,
      value: 'ManualTransactions',
      roles: [RoleEnum.ManualTransactionCostPriceWrite, RoleEnum.TransactionWrite, RoleEnum.TransactionAdmin, RoleEnum.TransactionRead, RoleEnum.SourceTransactionRead],
      isStoresSelect: true,
      isFranchiseSelect: false,
    },
  ];

  const { user } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store | null>(null);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [systemArea, setSystemArea] = useState<ISystemAreas | undefined>();
  const [areasToShow, setAreasToShow] = useState<ISystemAreas[]>([]);
  const { runSystemAuditingReport } = useNswagClient();
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);

  useEffect(() => {
    if (user?.roles?.includes(RoleEnum.Admin) || user?.roles?.includes(RoleEnum.AuditAdministrator)) {
      setAreasToShow(areas);
      setSystemArea(areas[0]);
    }
    else if (user?.roles?.includes(RoleEnum.AuditRead)) {
      const partialAreas = areas.filter(area =>
        area.roles.some(role => user?.roles?.includes(role)),
      );
      setAreasToShow(partialAreas);
      setSystemArea(partialAreas[0]);
    }
  }, []);

  const handleChange = (event: SelectChangeEvent) => {
    const area = areas.find(x => x.value == event.target.value);
    setSystemArea(area);
  };

  const getMaxDate = () => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleFromDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store | null) => {
    setSelectedStores(value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const from = new Date(new Date(fromDate).setHours(0, 0, 1, 0));
    const to = new Date(new Date(toDate).setHours(23, 59, 59, 0));

    let store: string | undefined = undefined;
    if (systemArea?.isStoresSelect) {
      store = selectedStores?.storeNumber ?? undefined;
    }

    let franchiseId = -1;
    if (systemArea?.isFranchiseSelect) {
      franchiseId = selectedStore?.franchiseId ?? -1;
    }

    const request: RequestSystemAuditingReportRequest = {
      from: formatDateForReport(from),
      to: formatDateForReport(to),
      store: store,
      franchise: franchiseId,
      systemAreaId: (systemArea?.key ?? 1) as AuditLogSystemArea,
    };

    runSystemAuditingReport(request).then(() => {
      setSuccessMessage(t('reporting.generic.post-generation-message'));
      setIsRequestSubmitted(true);
    }).catch((error) => {
      if (typeof (error) === 'string') {
        setErrorMessage(error);
      }
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  const isFormValid = (): boolean => {
    if (fromDate == '') return false;
    if (toDate == '') return false;
    if (systemArea?.isStoresSelect) {
      if (selectedStores == null) return false;
    }
    return true;
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={6}
          xs={12}>
          {systemArea?.isStoresSelect &&
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                limitTags={2}
                options={user?.stores ?? []}
                value={selectedStores}
                onChange={handleSelectChange}
                getOptionKey={(option) => option.storeNumber ?? ''}
                getOptionLabel={(option) => option.storeName ?? ''}
                renderInput={(params) => (
                  <TextField {...params}
                    required
                    label={t('selectStore')}
                    placeholder={t('selectStore')} />
                )}
              />
            </FormControl>}
          {systemArea?.isFranchiseSelect &&
            <Alert color='info'
              variant='filled'>{t('reporting.system-audit-report.noStores')}{selectedStore?.franchiseName}.</Alert>}
          {!systemArea?.isFranchiseSelect && !systemArea?.isStoresSelect &&
            <Alert color='info'
              variant='filled'>{t('reporting.system-audit-report.noStoresOrFranchise')}</Alert>}
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth
            variant="outlined">
            <InputLabel>{t('reporting.system-audit-report.systemArea')}</InputLabel>
            <Select value={systemArea?.value ?? ''}
              placeholder='Select system area'
              label="System area"
              onChange={handleChange}>
              {areasToShow.map((area) => (
                <MenuItem key={area.key}
                  value={area.value}>
                  {t(`reporting.system-audit-report.${area.value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.start-date')}
            type="date"
            required
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: getMinDate(), max: toDate != '' ? toDate : getMaxDate() } }}
            fullWidth
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.end-date')}
            type="date"
            required
            value={toDate}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: fromDate != '' ? fromDate : getMinDate(), max: getMaxDate() } }}
            fullWidth
          />
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage &&
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary'
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted || !isFormValid()}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default SystemAuditReportForm;